import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	effect,
	inject,
	input,
	output,
	signal,
	untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { JeptoBaseCardComponent } from '../jepto-base-card.component';
import { JeptoDateRangePickerComponent } from '../../filters/jepto-date-range-picker/jepto-date-range-picker.component';
import { JeptoDimensionFilterDropdownComponent } from '../../filters/jepto-dimension-filter-dropdown/jepto-dimension-filter-dropdown.component';
import { JeptoUiInputComponent } from '../../../../ui-lib/form-controls/jepto-ui-input/jepto-ui-input.component';
import { DataService } from '../../../services/data.service';
import { PageFilterService } from '../../../services/page-filter.service';
import { ControlType, type FilterCardConfig } from '../../../models/card-model';
import type { FilterCardRuntimeData } from '../../../models/page-filter.model';
import {
	CubeFilterOperator,
	isDimensionFilterCardData,
	shouldPublishPageFilter,
} from '../../../models/page-filter.model';
import { mergeDimensionFilterPreviewData } from '../../../utils/dimension-filter-merge.util';
import { warn } from '@jepto/shared/utils/logger';
import { resolveFilterCardDataSource } from '../../../utils/filter-data-source.util';
import {
	buildEmptyDimensionFilterDropdownData,
	buildPlaceholderDimensionFilterData,
} from '../../../utils/dimension-filter-merge.util';
import { resolveFilterControlType } from '../../../utils/filter-control-type.util';
import type { DateRangeExpression } from '../../../models/date-range-expression.model';
import {
	resolveSearchFilterLabel,
	resolveSearchFilterPlaceholder,
} from '../../editors/jepto-search-filter-card-editor/search-filter-card-editor.types';
import { buildSearchPageFilters } from '../../../utils/filter-data-source.util';

const SEARCH_PAGE_FILTER_DEBOUNCE_MS = 500;

function dateRangeExpressionsEqual(
	left: DateRangeExpression | undefined,
	right: DateRangeExpression | undefined
): boolean {
	if (left === undefined && right === undefined) {
		return true;
	}
	if (left === undefined || right === undefined) {
		return false;
	}
	if (left.length !== right.length) {
		return false;
	}
	return left.every((token, index) => token === right[index]);
}

/**
 * Filter Card Component
 *
 * Renders date range or dimension dropdown filter controls within a card container.
 * {@link FilterCardConfig.controlType} is the persisted control (like {@link ChartCardConfig.chartType}
 * on chart cards); the layout container passes a minimal `config` overlay from `CardList.controlType`.
 */
@Component({
	selector: 'jepto-filter-card',
	imports: [
		CommonModule,
		FormsModule,
		MatMenuModule,
		MatIconModule,
		JeptoDateRangePickerComponent,
		JeptoDimensionFilterDropdownComponent,
		JeptoUiInputComponent,
	],
	templateUrl: './jepto-filter-card.component.html',
	styleUrl: './jepto-filter-card.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		class: 'jepto-filter-card',
		'[class.jepto-filter-card--dropdown]':
			'resolvedControlType() === ControlType.Dropdown',
		'[class.jepto-filter-card--search]':
			'resolvedControlType() === ControlType.Search',
		'[class.jepto-filter-card--date-range]':
			'resolvedControlType() === ControlType.DateRange',
		'[class.jepto-filter-card--drag-passthrough]':
			'isEditMode() && !isSelected()',
	},
})
export class JeptoFilterCardComponent extends JeptoBaseCardComponent {
	readonly testId = input<string | undefined>();
	/** Saved card config + layout shell (see chart card `config` input). */
	readonly config = input<FilterCardConfig>();
	/**
	 * When true (e.g. dropdown filter editor create preview), show instructional
	 * empty state when no dimension is configured yet.
	 */
	readonly allowEmptyFilter = input<boolean>(false);
	/** Runtime filter payload from WebSocket (`cardData` / `DataService`). */
	readonly filterData = input<FilterCardRuntimeData>();
	/**
	 * Report-level default date range expression (view `dateRange` → page default).
	 * Drives the date-range picker in preview; ignored for dropdown filters.
	 */
	readonly reportDateRangeExpression = input<
		DateRangeExpression | undefined
	>();
	readonly configureFilterScope = output<void>();
	readonly dateRangeChange = output<DateRangeExpression>();

	private readonly dataService = inject(DataService);
	private readonly pageFilterService = inject(PageFilterService);
	private readonly destroyRef = inject(DestroyRef);

	/** Session picker value — set on Done before parent `reportDateRangeExpression` updates. */
	private readonly sessionPickerExpression = signal<
		DateRangeExpression | undefined
	>(undefined);
	private lastReportDateRangeExpression: DateRangeExpression | undefined;
	private readonly searchQueryInput$ = new Subject<string>();

	protected readonly ControlType = ControlType;

	constructor() {
		super();
		this.searchQueryInput$
			.pipe(
				debounceTime(SEARCH_PAGE_FILTER_DEBOUNCE_MS),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(value => this.#publishSearchQuery(value));

		effect(() => {
			const cardId = this.cardId();
			const controlType = this.resolvedControlType();
			if (!cardId || controlType !== ControlType.Search) {
				return;
			}

			// Re-run when stash changes (tab switch, debounced publish, clear).
			this.pageFilterService.searchPageFilters();
			const stashedQuery =
				this.pageFilterService.getSearchQueryForCard(cardId);
			untracked(() => {
				if (stashedQuery !== this.sessionSearchQuery()) {
					this.sessionSearchQuery.set(stashedQuery);
				}
			});
		});

		effect(() => {
			const report = this.reportDateRangeExpression();
			const session = this.sessionPickerExpression();

			if (
				report !== undefined &&
				this.lastReportDateRangeExpression !== undefined &&
				!dateRangeExpressionsEqual(
					report,
					this.lastReportDateRangeExpression
				)
			) {
				if (
					session &&
					!dateRangeExpressionsEqual(session, report)
				) {
					this.sessionPickerExpression.set(undefined);
				}
			}

			if (
				session &&
				report &&
				dateRangeExpressionsEqual(session, report)
			) {
				this.sessionPickerExpression.set(undefined);
			}

			this.lastReportDateRangeExpression = report;
		});
	}

	/**
	 * Effective config: merge CardService (saved editor/API) with layout `config` input.
	 * Layout passes ids and `controlType`; persisted config from the service wins for controlType
	 * when present (same precedence as `chartType` on {@link JeptoChartCardComponent}).
	 */
	protected readonly effectiveConfig = computed(
		(): FilterCardConfig | null => {
			const direct = this.config();
			const fromService = this.baseConfig() as FilterCardConfig | null;

			if (direct && fromService) {
				const overlay: Partial<FilterCardConfig> = {};
				for (const key of Object.keys(
					direct
				) as (keyof FilterCardConfig)[]) {
					const value = direct[key];
					if (value !== undefined) {
						(overlay as Record<string, unknown>)[key] = value;
					}
				}
				return {
					...fromService,
					...overlay,
					controlType:
						direct.controlType ?? fromService.controlType,
				};
			}
			if (direct) {
				return direct;
			}
			return fromService;
		}
	);

	protected readonly effectiveFilterData = computed(() => {
		const direct = this.filterData();
		if (direct) {
			return direct;
		}

		const id = this.cardId();
		if (id) {
			return this.dataService.filterDataSignal(id)();
		}
		return undefined;
	});

	/**
	 * Resolved UI control from persisted config + layout {@link CardList.controlType}.
	 * `null` until controlType is known — avoids flashing a date-range picker on search/dropdown cards.
	 */
	protected readonly resolvedControlType = computed((): ControlType | null => {
		try {
			return resolveFilterControlType({
				config: this.effectiveConfig() ?? undefined,
				layout: {
					controlType: this.config()?.controlType,
					cardId: this.cardId(),
				},
			});
		} catch (err) {
			warn('JeptoFilterCardComponent: missing controlType; pending render', {
				cardId: this.cardId(),
				error: err,
			});
			return null;
		}
	});

	protected readonly dimensionFilterData = computed(() => {
		const data = this.effectiveFilterData();
		const config = this.effectiveConfig();
		if (isDimensionFilterCardData(data)) {
			return config
				? mergeDimensionFilterPreviewData(data, config)
				: data;
		}
		if (this.resolvedControlType() !== ControlType.Dropdown) {
			return undefined;
		}
		const placeholder = config
			? buildPlaceholderDimensionFilterData(config)
			: undefined;
		if (placeholder) {
			return placeholder;
		}
		return this.allowEmptyFilter()
			? buildEmptyDimensionFilterDropdownData()
			: undefined;
	});

	protected readonly hasDimensionConfigured = computed(
		() => !!this.effectiveConfig()?.data?.dimensions?.[0]?.name?.trim()
	);

	protected readonly hasSearchFieldsConfigured = computed(() =>
		(this.effectiveConfig()?.data?.dimensions ?? []).some(d =>
			d.name?.trim()
		)
	);

	protected readonly searchPlaceholder = computed(() =>
		resolveSearchFilterPlaceholder(this.effectiveConfig() ?? {})
	);

	private readonly sessionSearchQuery = signal('');

	protected readonly searchInputReadonly = computed(() =>
		this.allowEmptyFilter()
	);

	protected readonly searchQuery = this.sessionSearchQuery.asReadonly();

	/** Editor create flow: instructional empty state when dimension is not set yet. */
	protected readonly showCreateDropdownEmptyState = computed(
		() =>
			this.allowEmptyFilter() &&
			this.resolvedControlType() === ControlType.Dropdown &&
			!this.hasDimensionConfigured()
	);

	protected readonly showCreateSearchEmptyState = computed(
		() =>
			this.allowEmptyFilter() &&
			this.resolvedControlType() === ControlType.Search &&
			!this.hasSearchFieldsConfigured()
	);

	protected readonly dateRange = computed(() => {
		const session = this.sessionPickerExpression();
		if (session) {
			return session;
		}
		const fromReport = this.reportDateRangeExpression();
		if (fromReport) {
			return fromReport;
		}
		const data = this.effectiveFilterData();
		if (!data || isDimensionFilterCardData(data)) {
			return undefined;
		}
		return data.timeDimension?.dateRange;
	});

	/**
	 * Picker display value. Edit grid shows the report-settings default only;
	 * preview/shared view uses session overrides via {@link dateRange}.
	 */
	protected readonly dateRangeForDisplay = computed(() => {
		if (this.isEditMode()) {
			return this.reportDateRangeExpression();
		}
		return this.dateRange();
	});

	protected readonly searchLabel = computed(() =>
		resolveSearchFilterLabel(this.effectiveConfig() ?? {})
	);

	protected onSearchQueryChange(value: string): void {
		this.sessionSearchQuery.set(value);
		this.pageFilterService.signalRefreshIntent();
		this.searchQueryInput$.next(value);
	}

	#publishSearchQuery(value: string): void {
		const cardId = this.cardId();
		const config = this.effectiveConfig();
		if (!cardId || !config) {
			return;
		}

		const filters = buildSearchPageFilters(config, cardId, value);
		if (filters.length === 0) {
			this.pageFilterService.clearSelection(cardId);
			return;
		}

		this.pageFilterService.setSearchPageFilters(cardId, filters);
	}

	protected onDateRangeChange(expression: DateRangeExpression): void {
		// Edit-mode template omits (dateRangeChange); preview/shared view handles session changes.
		this.sessionPickerExpression.set(expression);
		this.dateRangeChange.emit(expression);
	}

	protected onEditCardClick(event: MouseEvent): void {
		event.stopPropagation();
		this.editCard.emit();
	}

	onDimensionSelection(values: string[]): void {
		const cardId = this.cardId();
		if (!cardId) {
			throw new Error(
				'Filter card selection requires cardId. Ensure the layout passes cardId to jepto-filter-card.'
			);
		}

		const data = this.dimensionFilterData();
		if (!data) {
			throw new Error(
				`Filter card dropdown runtime data is required (cardId=${cardId}). Provide filterData input or hydrate via DataService.`
			);
		}

		const config = this.effectiveConfig();

		const allOptionValues = data.options.map(o => o.value);
		const member = data.dimension.member;
		const dataSource = resolveFilterCardDataSource(config ?? undefined, {
			member,
			cardId,
		});

		const runtime = this.effectiveFilterData();
		if (isDimensionFilterCardData(runtime)) {
			this.dataService.updateFilterData(cardId, {
				...runtime,
				selected: values,
			});
		}

		if (!shouldPublishPageFilter(values, allOptionValues)) {
			this.pageFilterService.clearSelection(cardId);
			return;
		}

		this.pageFilterService.setSelection({
			cardId,
			member,
			operator: CubeFilterOperator.Equals,
			values,
			dataSource,
			allOptionValues,
		});
	}
}
